import { useMemo } from 'react';
import styled from 'styled-components';

import { Metrics } from 'components/insights/constants';
import AnalyticsResponseRollupCollection from 'components/insights/query/AnalyticsResponseRollupCollection';
import { useBusinessDevelopmentStore } from 'stores/businessDevelopmentStore';

import BreakoutChart from './chart/BreakoutChart';
import useBusinessDevelopmentAnalytics from './query/useBusinessDevelopmentAnalytics';
import BusinessDevelopmentTableSection from './table/BusinessDevelopmentTableSection';
import MetricCardsRow from './tiles/MetricCardsRow';
import {
  buildCareTransitionsParams,
  buildDischargesParams,
  buildReferralsTableParams,
  TABLE_PIVOT,
  TablePivot,
  viewByDimension,
} from './constants';
import ViewBySelector from './ViewBySelector';

export type SharedAnalytics = {
  loading: boolean;
  rollups: AnalyticsResponseRollupCollection;
};

// Maps each pivot to the metric used for the breakout chart's primary bar value
export const PIVOT_CHART_METRIC: Record<string, Metrics> = {
  patientVolume: Metrics.LOCATION_EPISODE_ID_COUNT,
  discharges: Metrics.ID_COUNT,
  referrals: Metrics.REVIEW_ID_COUNT,
};

const BusinessDevelopmentDashboard = () => {
  const viewBy = useBusinessDevelopmentStore((state) => state.viewBy);
  const tablePivot = useBusinessDevelopmentStore((state) => state.tablePivot);
  const dimension = viewByDimension(viewBy);

  const careTransitionsParams = useMemo(() => buildCareTransitionsParams(dimension), [dimension]);
  const dischargesParams = useMemo(() => buildDischargesParams(dimension), [dimension]);
  const referralsParams = useMemo(() => buildReferralsTableParams(dimension), [dimension]);

  // Care transitions and discharges always fetch — they feed the metric cards (Volume, Discharges,
  // Readmission Rate) which are visible regardless of pivot. Referrals only feeds the table/chart,
  // so it's not requested until that pivot is selected.
  const careTransitions = useBusinessDevelopmentAnalytics(careTransitionsParams, {
    queryKey: 'bdCareTransitions',
  });

  const discharges = useBusinessDevelopmentAnalytics(dischargesParams, {
    queryKey: 'bdDischarges',
  });

  const referrals = useBusinessDevelopmentAnalytics(referralsParams, {
    queryKey: 'bdReferrals',
    enabled: tablePivot === TABLE_PIVOT.REFERRALS,
  });

  const activePivotMap: Record<TablePivot, SharedAnalytics> = {
    [TABLE_PIVOT.PATIENT_VOLUME]: careTransitions,
    [TABLE_PIVOT.DISCHARGES]: discharges,
    [TABLE_PIVOT.REFERRALS]: referrals,
  };
  const activePivot = activePivotMap[tablePivot];

  return (
    <DashboardWrapper>
      <ViewBySelector />
      <MetricsGrid>
        <MetricCardsRow careTransitions={careTransitions} discharges={discharges} />
      </MetricsGrid>
      <BreakoutChart data={activePivot} chartMetric={PIVOT_CHART_METRIC[tablePivot]} />
      <BusinessDevelopmentTableSection data={activePivot} />
    </DashboardWrapper>
  );
};

export default BusinessDevelopmentDashboard;

const DashboardWrapper = styled.div`
  display: flex;
  flex-direction: column;

  > * {
    margin: 0 auto 24px auto;
    max-width: 1280px;
    width: 100%;
  }
`;

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`;
